import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { api, smartApi, type CatchUpView, type ConversationView, type SmartMarks, type TriageView } from '../../api/client';
import { dateOnly, shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useMessagesLive } from '../../lib/messages';
import { Button } from '../ui';

/**
 * Smart messaging inside Messages: the Spam view, closed and reopened text conversations, archived
 * channels with Unarchive, each person's own Done / Snooze / Later, and Catch me up. Everything
 * comes from GET /messages/smart, already filtered to what this person may see (Done, Snooze and
 * Later are theirs alone). The smart service works in the background, so this loads again on
 * Messages' live events (a new text conversation is checked for spam a moment after it arrives),
 * when a snooze ends, and every minute while the page is in front.
 *
 * Messages.tsx uses it in a few marked lines: useSmart + SmartContext in the inbox, `hidden` on the
 * three lists, SmartSide in the sidebar, TextStateMark in a conversation's link, SmartTriage in a
 * conversation's header, SmartThreadBar under it, SmartThreadCatchUp in a thread's panel.
 */

/** Why the filter flagged a conversation, in plain words (codes from apps/smart/src/rules/spam.ts). */
export const SPAM_REASONS: Record<string, string> = {
  pattern: 'reads like a known scam or robotext',
  shortener: 'has a shortened link',
  'odd-link': 'links to a bare address or a throwaway site',
  repeated: 'the same text came from several numbers',
  marketing: 'is a bulk marketing text with a link'
};

export type TriageSet = { done?: boolean; snoozeUntil?: string | null; later?: boolean };

export interface SmartState {
  domain: string;
  marks: SmartMarks | null;
  /**
   * Left out of the lists: in Spam, emptied by retention (shows again with a new text), Done (until
   * a message newer than then: pass the conversation's lastSeq) or snoozed.
   */
  hidden: (id: string, lastSeq?: number) => boolean;
  /** For an empty list: when all of its conversations are Done, snoozed or in Spam, a line saying so. */
  emptyNote: (all: ConversationView[], kind: ConversationView['kind']) => string | null;
  spamOf: (id: string) => SmartMarks['spam'][number] | undefined;
  closedOf: (id: string) => SmartMarks['closed'][number] | undefined;
  archivingOf: (id: string) => SmartMarks['archiving'][number] | undefined;
  triageOf: (id: string) => TriageView | undefined;
  /** Done, Snooze, Later: this person's own. */
  setTriage: (id: string, set: TriageSet) => Promise<void>;
  /** Catch me up is on for the company. */
  catchUpOn: boolean;
  reload: () => void;
  notSpam: (id: string) => Promise<void>;
}

const NONE: SmartState = {
  domain: '',
  marks: null,
  hidden: () => false,
  emptyNote: () => null,
  spamOf: () => undefined,
  closedOf: () => undefined,
  archivingOf: () => undefined,
  triageOf: () => undefined,
  setTriage: async () => undefined,
  catchUpOn: false,
  reload: () => undefined,
  notSpam: async () => undefined
};

export const SmartContext = createContext<SmartState>(NONE);

const isDone = (t: TriageView | undefined, lastSeq: number | undefined) => Boolean(t && t.doneSeq !== null && lastSeq !== undefined && lastSeq <= t.doneSeq);
const isSnoozed = (t: TriageView | undefined, now = Date.now()) => Boolean(t?.snoozedUntil && Date.parse(t.snoozedUntil) > now);

export function useSmart(domain: string): SmartState {
  const [marks, setMarks] = useState<SmartMarks | null>(null);
  /** Bumped when a snooze ends, so the lists draw again. */
  const [tick, setTick] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  const load = useCallback(async () => {
    try {
      const next = await smartApi.marks(domain);
      // The same as before (most minutes): nothing on the page needs to draw again.
      setMarks((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    } catch {
      // Nothing shown: Messages works as it does without smart messaging.
    }
  }, [domain]);
  const soon = useCallback(
    (ms: number) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), ms);
    },
    [load]
  );
  useEffect(() => {
    void load();
    const every = window.setInterval(() => document.visibilityState === 'visible' && void load(), 60_000);
    return () => {
      window.clearInterval(every);
      window.clearTimeout(timer.current);
    };
  }, [load]);

  // When the next snooze ends, its conversation comes back.
  useEffect(() => {
    const now = Date.now();
    const next = Math.min(...(marks?.mine ?? []).map((t) => (t.snoozedUntil ? Date.parse(t.snoozedUntil) : Infinity)).filter((x) => x > now));
    if (!Number.isFinite(next)) return;
    const t = window.setTimeout(() => setTick((n) => n + 1), Math.min(next - now + 500, 2 ** 31 - 1));
    return () => window.clearTimeout(t);
  }, [marks, tick]);

  const closed = useMemo(() => new Map((marks?.closed ?? []).map((x) => [x.conversationId, x])), [marks]);
  useMessagesLive((e) => {
    if (e.type === 'reset') return soon(300);
    if (e.tenant !== domain) return;
    // A conversation started or changed: the spam filter looks at a new one a moment later.
    if (e.type === 'conversation') return soon(1_500);
    if (e.type === 'message' && e.kind === 'text' && e.conversationId) {
      // A new text in a closed conversation opens it again, straight away.
      const c = closed.get(e.conversationId);
      if (c?.state === 'closed') setMarks((m) => (m ? { ...m, closed: m.closed.map((x) => (x.conversationId === e.conversationId ? { ...x, state: 'reopened' } : x)) } : m));
      if (marks?.emptied.includes(e.conversationId)) setMarks((m) => (m ? { ...m, emptied: m.emptied.filter((x) => x !== e.conversationId) } : m));
      soon(1_500);
    }
  });

  return useMemo<SmartState>(() => {
    const spam = new Map((marks?.spam ?? []).map((x) => [x.conversationId, x]));
    const emptied = new Set(marks?.emptied ?? []);
    const archiving = new Map((marks?.archiving ?? []).map((x) => [x.id, x]));
    const mine = new Map((marks?.mine ?? []).map((x) => [x.conversationId, x]));
    const now = Date.now();
    return {
      domain,
      marks,
      hidden: (id, lastSeq) => spam.has(id) || emptied.has(id) || isDone(mine.get(id), lastSeq) || isSnoozed(mine.get(id), now),
      emptyNote: (all, kind) =>
        all.some((c) => c.kind === kind && (spam.has(c.id) || isDone(mine.get(c.id), c.lastSeq) || isSnoozed(mine.get(c.id), now)))
          ? kind === 'text'
            ? 'All of them are Done, snoozed or in Spam for now: see below.'
            : 'All of them are Done or snoozed for now: see below.'
          : null,
      spamOf: (id) => spam.get(id),
      closedOf: (id) => closed.get(id),
      archivingOf: (id) => archiving.get(id),
      triageOf: (id) => mine.get(id),
      setTriage: async (id, set) => {
        const r = await smartApi.triage(domain, id, set);
        setMarks((m) => (m ? { ...m, mine: [...(m.mine ?? []).filter((x) => x.conversationId !== id), r.triage] } : m));
      },
      catchUpOn: Boolean(marks?.catchUp),
      reload: () => void load(),
      notSpam: async (id) => {
        await smartApi.notSpam(domain, id);
        setMarks((m) => (m ? { ...m, spam: m.spam.filter((x) => x.conversationId !== id) } : m));
        void load();
      }
    };
    // `tick` redraws when a snooze ends.
  }, [marks, closed, load, domain, tick]);
}

/** "Closed" or "Reopened", beside a text conversation in the list. */
export function TextStateMark({ id }: { id: string }) {
  const c = useContext(SmartContext).closedOf(id);
  if (!c) return null;
  return <span className={`smart-mark ${c.state}`}>{c.state === 'closed' ? 'Closed' : 'Reopened'}</span>;
}

/** The next whole hour from now, plus `hours`. */
function inHours(hours: number): Date {
  const d = new Date(Date.now() + hours * 3600_000);
  d.setMinutes(0, 0, 0);
  if (d.getTime() <= Date.now()) d.setHours(d.getHours() + 1);
  return d;
}
/** 9:00 in the morning, `days` from today (on this device's clock). */
function morning(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  return d;
}
/** Next Monday at 9:00. */
const nextWeek = () => morning(((8 - new Date().getDay()) % 7) || 7);

/** Done, Snooze and Later, for the conversation on screen: the person's own, never seen by anyone else. */
export function SmartTriage({ c }: { c: ConversationView }) {
  const smart = useContext(SmartContext);
  const [menu, setMenu] = useState(false);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!smart.marks) return null;
  const t = smart.triageOf(c.id);
  const done = isDone(t, c.lastSeq);
  const snoozed = isSnoozed(t);
  const later = Boolean(t?.laterAt);
  const run = async (set: TriageSet) => {
    setBusy(true);
    setError('');
    try {
      await smart.setTriage(c.id, set);
      setMenu(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };
  const snooze = (d: Date) => void run({ snoozeUntil: d.toISOString() });
  const snoozedUntil = snoozed ? `Snoozed until ${shortWhen(t!.snoozedUntil!)}` : '';
  return (
    <span className="smart-triage">
      <Button className="smart-wide" size="small" variant="ghost" icon="check" aria-pressed={done} disabled={busy} title={done ? 'Back in your list' : 'Out of your list until something new comes'} onClick={() => void run({ done: !done })}>
        {done ? 'Not done' : 'Done'}
      </Button>
      <span className="smart-snooze">
        <Button className="smart-wide" size="small" variant="ghost" icon="clock" aria-expanded={menu} aria-haspopup="menu" disabled={busy} title={snoozedUntil || 'Out of your list until a time you choose'} onClick={() => setMenu(!menu)}>
          {snoozedUntil || 'Snooze'}
        </Button>
        {/* On a phone: one button for all three, so the header keeps room for the conversation's name. */}
        <button
          type="button"
          className="msg-tool big smart-narrow"
          aria-label={done ? 'Done (Snooze or Later)' : snoozedUntil ? `${snoozedUntil} (Done or Later)` : later ? 'On Later (Done or Snooze)' : 'Done, Snooze or Later'}
          title="Done, Snooze or Later"
          aria-expanded={menu}
          aria-haspopup="menu"
          disabled={busy}
          onClick={() => setMenu(!menu)}
        >
          <Icon name={snoozed ? 'clock' : later && !done ? 'pin' : 'check'} size={17} />
        </button>
        {menu && (
          <div className="smart-menu" role="menu" aria-label="Snooze until">
            <button type="button" role="menuitem" className="smart-menu-phone" onClick={() => void run({ done: !done })}>
              {done ? 'Not done' : 'Done (until something new)'}
            </button>
            <button type="button" role="menuitem" className="smart-menu-phone" onClick={() => void run({ later: !later })}>
              {later ? 'Take off Later' : 'Save for later'}
            </button>
            <button type="button" role="menuitem" onClick={() => snooze(inHours(1))}>
              In an hour
            </button>
            <button type="button" role="menuitem" onClick={() => snooze(inHours(3))}>
              In 3 hours
            </button>
            <button type="button" role="menuitem" onClick={() => snooze(morning(1))}>
              Tomorrow morning
            </button>
            <button type="button" role="menuitem" onClick={() => snooze(nextWeek())}>
              Next week
            </button>
            <label className="smart-menu-custom">
              <span>Until</span>
              <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Snooze until a date and time" />
              <Button size="small" disabled={!custom || busy} onClick={() => custom && snooze(new Date(custom))}>
                Snooze
              </Button>
            </label>
            {snoozed && (
              <button type="button" role="menuitem" onClick={() => void run({ snoozeUntil: null })}>
                Unsnooze
              </button>
            )}
          </div>
        )}
      </span>
      <Button className="smart-wide" size="small" variant="ghost" icon="pin" aria-pressed={later} disabled={busy} title={later ? 'On your Later list' : 'Save it to your Later list'} onClick={() => void run({ later: !later })}>
        {later ? 'On Later' : 'Later'}
      </Button>
      {error && <span className="smart-error">{error}</span>}
    </span>
  );
}

/** A catch-up's bullet points, as text (never HTML). */
function Summary({ text }: { text: string }) {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const bullets = lines.every((l) => /^[-•*]\s/.test(l));
  return bullets ? (
    <ul>
      {lines.map((l, i) => (
        <li key={i}>{l.replace(/^[-•*]\s+/, '')}</li>
      ))}
    </ul>
  ) : (
    <>
      {lines.map((l, i) => (
        <p key={i}>{l}</p>
      ))}
    </>
  );
}

/**
 * Under a conversation's header: why it's in Spam (with Not spam), closed or reopened, a channel's
 * archive date, and Catch me up on what was new when it was opened (a summary by AI, for this
 * person only).
 */
export function SmartThreadBar({ c }: { c: ConversationView }) {
  const smart = useContext(SmartContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Where they'd read up to when they opened it (reading it moves that on straight away).
  const since = useRef(c.lastReadSeq);
  // What was unread then: others' messages in the conversation (replies only in a thread aren't counted).
  const unreadThen = useRef(c.unread);
  const [catchUp, setCatchUp] = useState<CatchUpView | null>(null);
  const [catching, setCatching] = useState(false);
  const [catchError, setCatchError] = useState('');
  const spam = smart.spamOf(c.id);
  const closed = c.kind === 'text' ? smart.closedOf(c.id) : undefined;
  const archiving = c.kind === 'channel' && !c.archived ? smart.archivingOf(c.id) : undefined;
  const fresh = Math.min(unreadThen.current, c.lastSeq - since.current);
  const offerCatchUp = smart.catchUpOn && fresh > 0 && !catchUp;
  if (!spam && !closed && !archiving && !offerCatchUp && !catchUp) return null;

  async function runCatchUp() {
    setCatching(true);
    setCatchError('');
    try {
      setCatchUp((await smartApi.catchUp(smart.domain, c.id, since.current)).catchUp);
    } catch (e) {
      setCatchError(e instanceof Error ? e.message : 'The catch-up didn’t come back.');
    } finally {
      setCatching(false);
    }
  }

  return (
    <div className="smart-bar" role="status">
      {spam && (
        <div className="smart-bar-row spam">
          <Icon name="flag" size={16} />
          <span>
            In Spam: this {(spam.reasons.map((r) => SPAM_REASONS[r]).filter(Boolean) as string[]).join(', and ') || 'looks like spam'}. Nothing was deleted.
            {error && <strong className="smart-error"> {error}</strong>}
          </span>
          <Button
            size="small"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await smart.notSpam(c.id);
              } catch (e) {
                setError(e instanceof Error ? e.message : 'That didn’t work.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Not spam
          </Button>
        </div>
      )}
      {closed && (
        <div className="smart-bar-row">
          <Icon name="check" size={16} />
          <span>
            {closed.state === 'closed'
              ? `Closed on ${dateOnly(closed.closedAt)} after going quiet. It opens again by itself when there’s a new text.`
              : `Reopened: a new text came after it closed on ${dateOnly(closed.closedAt)}.`}
          </span>
        </div>
      )}
      {archiving && (
        <div className="smart-bar-row">
          <Icon name="clock" size={16} />
          <span>Nobody has posted here lately, so Stitch will archive #{c.title} on {dateOnly(archiving.dueAt)} unless someone posts before then.</span>
        </div>
      )}
      {offerCatchUp && (
        <div className="smart-bar-row ai">
          <Icon name="spark" size={16} />
          <span>
            {fresh === 1 ? '1 new message' : `${fresh} new messages`} since you last read.
            {catchError && <strong className="smart-error"> {catchError}</strong>}
          </span>
          <Button size="small" disabled={catching} onClick={() => void runCatchUp()}>
            {catching ? 'Catching up…' : 'Catch me up'}
          </Button>
        </div>
      )}
      {catchUp && <CatchUpPanel catchUp={catchUp} empty="Nothing new since you last read." onClose={() => setCatchUp(null)} />}
    </div>
  );
}

/** The AI summary panel, for this person only. */
export function CatchUpPanel({ catchUp, empty, onClose }: { catchUp: CatchUpView; empty: string; onClose: () => void }) {
  return (
    <section className="smart-catchup" aria-label="Catch-up" data-private="">
      <div className="smart-catchup-head">
        <Icon name="spark" size={16} />
        <strong>Catch-up</strong>
        <span className="smart-ai-label">Written by AI (Claude) · only you see this</span>
        <button type="button" className="icon-btn" aria-label="Close the catch-up" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </div>
      {catchUp.summary ? <Summary text={catchUp.summary} /> : <p>{empty}</p>}
      {catchUp.summary && <small className="muted">From {catchUp.messages === 1 ? '1 message' : `${catchUp.messages} messages`}. AI can get things wrong: check the messages before you act.</small>}
    </section>
  );
}

/**
 * In a thread's panel (one marked line in Messages.tsx): Catch me up on the thread, the message that
 * started it and its replies, once it has a few replies and the company has catch-ups on.
 */
export function SmartThreadCatchUp({ c, rootId, replies }: { c: ConversationView; rootId: string; replies: number }) {
  const smart = useContext(SmartContext);
  const [catchUp, setCatchUp] = useState<CatchUpView | null>(null);
  const [catching, setCatching] = useState(false);
  const [error, setError] = useState('');
  if (!smart.catchUpOn || (replies < 3 && !catchUp)) return null;
  return (
    <div className="smart-bar" role="status">
      {!catchUp && (
        <div className="smart-bar-row ai">
          <Icon name="spark" size={16} />
          <span>
            {replies} replies in this thread.
            {error && <strong className="smart-error"> {error}</strong>}
          </span>
          <Button
            size="small"
            disabled={catching}
            onClick={async () => {
              setCatching(true);
              setError('');
              try {
                setCatchUp((await smartApi.catchUp(smart.domain, c.id, undefined, rootId)).catchUp);
              } catch (e) {
                setError(e instanceof Error ? e.message : 'The catch-up didn’t come back.');
              } finally {
                setCatching(false);
              }
            }}
          >
            {catching ? 'Catching up…' : 'Catch me up'}
          </Button>
        </div>
      )}
      {catchUp && <CatchUpPanel catchUp={catchUp} empty="Nothing to catch up on in this thread yet." onClose={() => setCatchUp(null)} />}
    </div>
  );
}

const titleOf = (c: ConversationView) => (c.kind === 'channel' ? `#${c.title}` : c.title);

/**
 * In the sidebar, under Texts: the person's Later list, what they've snoozed and marked done, the
 * Spam view (flagged text conversations, with their unread counts) and archived channels with
 * Unarchive for admins. Each shows only when there's something in it.
 */
export function SmartSide({ domain, base, conversations, admin, onChanged }: { domain: string; base: string; conversations: ConversationView[]; admin: boolean; onChanged: () => void }) {
  const smart = useContext(SmartContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState<'' | 'spam' | 'archived' | 'later' | 'snoozed' | 'done'>('');
  const [busy, setBusy] = useState('');
  const m = smart.marks;
  if (!m) return null;
  const byId = new Map(conversations.map((c) => [c.id, c]));
  const spam = m.spam.map((s) => ({ s, c: byId.get(s.conversationId) })).filter((x): x is { s: SmartMarks['spam'][number]; c: ConversationView } => Boolean(x.c));
  const unread = spam.reduce((n, x) => n + x.c.unread, 0);
  const mine = (m.mine ?? []).map((t) => ({ t, c: byId.get(t.conversationId) })).filter((x): x is { t: TriageView; c: ConversationView } => Boolean(x.c));
  const later = mine.filter((x) => x.t.laterAt).sort((a, b) => (b.t.laterAt ?? '').localeCompare(a.t.laterAt ?? ''));
  const snoozed = mine.filter((x) => isSnoozed(x.t)).sort((a, b) => (a.t.snoozedUntil ?? '').localeCompare(b.t.snoozedUntil ?? ''));
  const done = mine.filter((x) => isDone(x.t, x.c.lastSeq)).sort((a, b) => (b.t.doneAt ?? '').localeCompare(a.t.doneAt ?? ''));
  if (!spam.length && !m.archived.length && !later.length && !snoozed.length && !done.length) return null;

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    try {
      await fn();
    } finally {
      setBusy('');
    }
  };
  const unarchive = (id: string) =>
    act(id, async () => {
      await api.updateChannel(domain, id, { archived: false });
      smart.reload();
      onChanged();
      navigate(`${base}/${id}`);
    });
  const toggle = (k: typeof open) => setOpen(open === k ? '' : k);
  // Plain functions (not components), so a list drawing again keeps focus where it was.
  const toggleButton = (k: typeof open, icon: 'pin' | 'clock' | 'check' | 'flag' | 'layers', label: string, n: number, badge?: number) => (
    <button type="button" className={`smart-side-toggle${open === k ? ' open' : ''}`} aria-expanded={open === k} onClick={() => toggle(k)}>
      <Icon name={icon} size={15} />
      <span>{label}</span>
      <span className="smart-count">{n}</span>
      {badge ? (
        <span className="msg-badge">
          {badge > 99 ? '99+' : badge}
          <span className="sr-only"> unread</span>
        </span>
      ) : null}
    </button>
  );
  /** A conversation in one of the lists, with one way to undo. */
  const row = (c: ConversationView, note: string, undoLabel: string, undo: () => void) => (
    <li key={c.id}>
      <NavLink to={`${base}/${c.id}`} className="smart-archived-name">
        {titleOf(c)}
        <small className="muted">{note}</small>
      </NavLink>
      <Button size="small" variant="ghost" disabled={busy !== ''} aria-label={`${undoLabel}: ${titleOf(c)}`} onClick={undo}>
        {busy === c.id ? '…' : undoLabel}
      </Button>
    </li>
  );

  return (
    <div className="msg-side-section smart-side">
      {later.length > 0 && (
        <>
          {toggleButton('later', 'pin', 'Later', later.length)}
          {open === 'later' && (
            <ul className="smart-archived" aria-label="Later">
              {later.map(({ t, c }) => (
                row(c, `Saved ${dateOnly(t.laterAt!)}`, 'Remove', () => void act(c.id, () => smart.setTriage(c.id, { later: false })))
              ))}
            </ul>
          )}
        </>
      )}
      {snoozed.length > 0 && (
        <>
          {toggleButton('snoozed', 'clock', 'Snoozed', snoozed.length)}
          {open === 'snoozed' && (
            <ul className="smart-archived" aria-label="Snoozed">
              {snoozed.map(({ t, c }) => (
                row(c, `Back ${shortWhen(t.snoozedUntil!)}`, 'Unsnooze', () => void act(c.id, () => smart.setTriage(c.id, { snoozeUntil: null })))
              ))}
            </ul>
          )}
        </>
      )}
      {done.length > 0 && (
        <>
          {toggleButton('done', 'check', 'Done', done.length)}
          {open === 'done' && (
            <ul className="smart-archived" aria-label="Done">
              {done.map(({ t, c }) => (
                row(c, `Done ${dateOnly(t.doneAt ?? new Date().toISOString())}; back with anything new`, 'Not done', () => void act(c.id, () => smart.setTriage(c.id, { done: false })))
              ))}
            </ul>
          )}
        </>
      )}
      {spam.length > 0 && (
        <>
          {toggleButton('spam', 'flag', 'Spam', spam.length, unread)}
          {open === 'spam' && (
            <div className="msg-side-list" aria-label="Spam">
              {spam.map(({ s, c }) => (
                <NavLink key={c.id} to={`${base}/${c.id}`} className={`msg-link${c.unread ? ' unread' : ''}`} title={`Flagged ${dateOnly(s.flaggedAt)}`}>
                  <span className="msg-link-icon" aria-hidden="true">
                    <Icon name="flag" size={14} />
                  </span>
                  <span className="msg-link-title">{c.title}</span>
                </NavLink>
              ))}
              <p className="msg-side-empty">Likely spam and robotexts. Open one and choose Not spam to put it back.</p>
            </div>
          )}
        </>
      )}
      {m.archived.length > 0 && (
        <>
          {toggleButton('archived', 'layers', 'Archived channels', m.archived.length)}
          {open === 'archived' && (
            <ul className="smart-archived" aria-label="Archived channels">
              {m.archived.map((a) => (
                <li key={a.id}>
                  <NavLink to={`${base}/${a.id}`} className="smart-archived-name" title={a.auto ? 'Archived after going quiet' : `Archived by ${a.archivedBy ?? 'an admin'}`}>
                    #{a.name}
                    <small className="muted">{a.auto ? `Quiet, archived ${dateOnly(a.archivedAt)}` : `Archived ${dateOnly(a.archivedAt)}`}</small>
                  </NavLink>
                  {admin && (
                    <Button size="small" variant="ghost" disabled={busy !== ''} aria-label={`Unarchive #${a.name}`} onClick={() => void unarchive(a.id)}>
                      {busy === a.id ? '…' : 'Unarchive'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
